import { Logo } from "@components/Logo";
import { OrgSwitcher } from "@features/organisations";
import ArrowBackIcon from "@mui/icons-material/ArrowBackRounded";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { Box, Button, Divider, IconButton, Tooltip } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useLayoutStore } from "@stores/layoutStore";
import { useNavigate } from "@tanstack/react-router";
import { ProjectSurfaceNav } from "./ProjectSurfaceNav";
import { ProjectSwitcher } from "./ProjectSwitcher";
import { SidebarCredits } from "./SidebarCredits";
import { SidebarUser } from "./SidebarUser";

interface ProjectSidebarContentProps {
  projectId: string;
  showPinToggle?: boolean;
}

/**
 * Project-mode sidebar — rendered when the URL matches /projects/$id/*.
 *
 * Replaces the global nav with project-scoped surfaces (Overview, Chat,
 * BOM, Graph, Versions) and a workspace switcher at the top so the user
 * can hop between projects without losing the surface they're on.
 */
export function ProjectSidebarContent({
  projectId,
  showPinToggle,
}: ProjectSidebarContentProps) {
  const navigate = useNavigate();
  const isStreaming = useChatStore((s) => s.isStreaming);
  const pin = useLayoutStore((s) => s.appNavPin);
  const setPin = useLayoutStore((s) => s.setAppNavPin);
  const isPinned = pin === "expanded";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
        gap: 1.5,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Brand row + collapse affordance */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.5,
          py: 0.5,
          minHeight: 40,
        }}
      >
        <Logo size={40} pulseEmber={isStreaming} />
        {showPinToggle && (
          <Tooltip
            title={
              isPinned
                ? "Unpin sidebar (auto-collapse on chat)"
                : "Pin sidebar open everywhere"
            }
          >
            <IconButton
              size="small"
              onClick={() => setPin(isPinned ? "auto" : "expanded")}
              aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
              aria-pressed={isPinned}
              sx={{
                color: isPinned ? "primary.light" : "text.secondary",
                transition: (t) => `color ${t.motion.fast}`,
              }}
            >
              {isPinned ? (
                <PushPinIcon sx={{ fontSize: 18 }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Organisation context — kept inside the project sidebar so the
          user can still switch orgs and reach `/settings/organisations`
          without having to bounce back through `/projects`. The org's
          "Manage organisations" menu item is the only path into the
          settings tree from inside a project workspace. */}
      <OrgSwitcher />

      {/* Workspace switcher */}
      <ProjectSwitcher projectId={projectId} />

      {/* Quick exit back to the project list */}
      <Button
        size="small"
        variant="text"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={() => void navigate({ to: "/projects" })}
        sx={{
          alignSelf: "flex-start",
          color: "text.secondary",
          textTransform: "none",
          fontSize: "0.75rem",
          px: 1,
          py: 0.25,
          minWidth: 0,
        }}
      >
        All projects
      </Button>

      <Divider sx={{ my: 0.5, borderColor: "border.default" }} />

      {/* Surface nav. Per-board navigation lives inside this component
          now (each Board is an expandable group with its own surfaces),
          which subsumes what the standalone `ProjectBoardPicker`
          previously did for multi-board projects. */}
      <ProjectSurfaceNav projectId={projectId} />

      <Box sx={{ flexGrow: 1 }} />

      {/* Workspace footer — user menu + usage pill */}
      <Box
        sx={{
          mx: -2,
          mb: -2,
          px: 2,
          pt: 2,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "border.default",
          bgcolor: "surface.sunken",
        }}
      >
        <SidebarUser />
        <SidebarCredits />
      </Box>
    </Box>
  );
}
